import { useCallback } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Spend } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Button, Modal, Tooltip } from "@similarweb/ui-components";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import { CampaignsBanner } from "app/components/campaigns/banners/CampaignsBanner";
import { KeywordsLimitBanner } from "app/components/campaigns/banners/KeywordsLimitBanner";
import { onUpgradeHookClick } from "app/components/campaigns/shared/CampaignsUtils";
import { KeywordsGroupImportCombobox } from "app/pages/digital-marketing/common/components/KeywordsGroups/KeywordsGroupImportCombobox";
import { useKeywordsCampaignsContext } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsContext";
import { getRankTrackerCampaignsAndKeywordsSubtitle } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsInfraUtils";
import type {
    IKeywordsCampaignsScrapingKeywords,
    IRTCurrentCampaignData,
    IRTUpdateCampaignTableData,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import AddTagsCombobox from "app/pages/digital-marketing/keywords-campaigns/shared/components/AddTagsCombobox";
import { ImportGscModal } from "app/pages/digital-marketing/keywords-campaigns/shared/components/ImportGscModal";
import { KeywordsImportErrorAlert } from "app/pages/digital-marketing/keywords-campaigns/shared/components/KeywordsImportErrorAlert";
import { KeywordsTextarea } from "app/pages/digital-marketing/keywords-campaigns/shared/components/KeywordsTextarea";
import { useGscAuthGate } from "app/pages/digital-marketing/keywords-campaigns/shared/hooks/useGscAuthGate";
import { allTrackers } from "app/services/track/track";

import { ConfigurationHeader } from "./ConfigurationHeader";
import { TRACKING_EVENT_NAME } from "./constants";
import { useAddKeywordsModal } from "./hooks/useAddKeywordsModal";
import { useCountNewTrackedSearches } from "./hooks/useCountNewTrackedSearches";
import { useIsKeywordsLimitExceeded } from "./hooks/useIsKeywordsLimitExceeded";
import { ModalContent } from "./styles";

type Props = {
    campaignConfiguration: IRTCurrentCampaignData;
    onClose: () => void;
    mutate: (payload: IRTUpdateCampaignTableData, trackedSearches: number) => void;
    isPending: boolean;
};

// Rendered inside <GAContextProviders> so useGscAuthGate binds to ga-connect's
// QueryClient, while useKeywordsMutation in the parent uses the app's QueryClient.
export const AddKeywordsModalContent = ({ campaignConfiguration, onClose, mutate, isPending }: Props) => {
    const { translate } = useTranslate();
    const { locationsAndLanguagesLists, campaignsConsumptionMetrics, myUserConsumptionMetrics } =
        useKeywordsCampaignsContext();

    const { configuration, keywords, tags, fixedGscConfig } = useAddKeywordsModal(
        campaignConfiguration,
        locationsAndLanguagesLists?.languagesList ?? [],
    );

    const addedTrackedSearches = useCountNewTrackedSearches(
        keywords.allKeywords,
        configuration.resolvedScrapingConfigs,
    );
    const isLimitExceeded = useIsKeywordsLimitExceeded(addedTrackedSearches);

    const {
        setIsGscModalOpen,
        handleGscImport: importGsc,
        handleCsvImport: importCsv,
        handleKeywordListImport: importKeywordList,
    } = keywords;
    const { handleAddTag: addTag } = tags;

    const handleOpenGscModal = useCallback(() => {
        if (fixedGscConfig) {
            setIsGscModalOpen(true);
        }
    }, [fixedGscConfig, setIsGscModalOpen]);

    const { trigger: triggerOpenGscModal, isConnecting: isGscConnecting } = useGscAuthGate(handleOpenGscModal);

    const handleGscImportClick = useCallback(() => {
        allTrackers.dispatchTrackEvent("Button", "click", `${TRACKING_EVENT_NAME}/GSC import clicked`);
        triggerOpenGscModal();
    }, [triggerOpenGscModal]);

    const handleGscImportSubmit = useCallback(
        (...args: Parameters<typeof importGsc>) => {
            allTrackers.dispatchTrackEvent("Button", "click", `${TRACKING_EVENT_NAME}/GSC import submitted`);
            importGsc(...args);
        },
        [importGsc],
    );

    const handleCsvImport = useCallback(
        (...args: Parameters<typeof importCsv>) => {
            allTrackers.dispatchTrackEvent("Button", "click", `${TRACKING_EVENT_NAME}/CSV imported`);
            importCsv(...args);
        },
        [importCsv],
    );

    const handleTagAdded = useCallback(
        (...args: Parameters<typeof addTag>) => {
            allTrackers.dispatchTrackEvent("Drop Down", "click", `${TRACKING_EVENT_NAME}/Tag added`);
            addTag(...args);
        },
        [addTag],
    );

    const handleKeywordListApply = useCallback(
        (...args: Parameters<typeof importKeywordList>) => {
            allTrackers.dispatchTrackEvent("Drop Down", "click", `${TRACKING_EVENT_NAME}/Keyword list applied`);
            importKeywordList(...args);
        },
        [importKeywordList],
    );

    const handleSubmit = useCallback(() => {
        allTrackers.dispatchTrackEvent("Button", "click", `${TRACKING_EVENT_NAME}/Submitted`, {
            keywordsCount: keywords.totalKeywordsCount,
            device: configuration.selectedDevice,
            locationCount: configuration.selectedLocationIds.size,
            importSourceTypes: keywords.importSources.map((s) => s.type).join(", "),
            tagsCount: tags.selectedTags.length,
            trackedSearchesAdded: addedTrackedSearches,
        });

        const keywordChanges = configuration.resolvedScrapingConfigs
            .filter((config) => config.id != null)
            .map((config) => ({
                scrapingGuid: config.id!,
                addedKeywords: keywords.allKeywords.map((kw) => kw.value),
            }));

        const updatedTags: IKeywordsCampaignsScrapingKeywords = {};
        for (const kw of keywords.allKeywords) {
            const combinedTags = [...new Set([...kw.tags, ...tags.selectedTags])];
            if (combinedTags.length > 0) {
                updatedTags[kw.value] = combinedTags;
            }
        }

        mutate({ keywordChanges, updatedTags }, addedTrackedSearches);
    }, [
        addedTrackedSearches,
        configuration.resolvedScrapingConfigs,
        configuration.selectedDevice,
        configuration.selectedLocationIds,
        keywords.allKeywords,
        keywords.importSources,
        keywords.totalKeywordsCount,
        tags.selectedTags,
        mutate,
    ]);

    const canSubmit = keywords.totalKeywordsCount > 0 && !isPending && !isLimitExceeded;

    const { name, asset: favicon } = campaignConfiguration;
    const bannerSubtitle = getRankTrackerCampaignsAndKeywordsSubtitle(campaignsConsumptionMetrics, {
        ...myUserConsumptionMetrics,
        usedKeywords: myUserConsumptionMetrics.usedKeywords + addedTrackedSearches,
    });

    return (
        <>
            <Modal.Header withBorder>
                <Modal.Title>{translate("rankTracker.addKeywords.modal.title")}</Modal.Title>
            </Modal.Header>

            <ModalContent>
                <ConfigurationHeader
                    favicon={favicon}
                    campaignName={name}
                    configuration={configuration}
                />

                {keywords.hasImportError ? (
                    <KeywordsImportErrorAlert onDismiss={() => keywords.setHasImportError(false)} />
                ) : null}

                <KeywordsTextarea
                    // Increase text input height, shrinks to 50vh on small screens, never below 128px
                    height="clamp(128px, 50vh, 256px)"
                    manualSource={keywords.manualSource}
                    importSources={keywords.importSources}
                    onManualInput={keywords.handleManualInput}
                    onCsvImport={handleCsvImport}
                    onRemoveImport={keywords.handleRemoveImport}
                    onImportFromGscClick={handleGscImportClick}
                    isImportFromGscDisabled={!fixedGscConfig || isGscConnecting}
                    isImporting={keywords.isImporting}
                    onImportError={() => keywords.setHasImportError(true)}
                >
                    <KeywordsGroupImportCombobox
                        addedIds={keywords.addedKeywordListIds}
                        onApply={handleKeywordListApply}
                    />
                </KeywordsTextarea>

                <Stack
                    gap="12px"
                    data-automation="add-keywords-modal-tags"
                >
                    <Text
                        fontVariant="body2"
                        color={colorsPalettes.carbon[300]}
                    >
                        {translate("rankTracker.addKeywords.modal.tagsLabel")}
                    </Text>
                    <AddTagsCombobox
                        isOpen={tags.isComboboxOpen}
                        onOpenChange={tags.setIsComboboxOpen}
                        selectedChips={tags.selectedTags}
                        onCloseChip={tags.handleRemoveTag}
                        searchValue={tags.searchValue}
                        onSearchValueChange={tags.setSearchValue}
                        options={tags.visibleTagOptions}
                        showCreateOption={tags.showCreateOption}
                        onOptionClick={handleTagAdded}
                    />
                </Stack>

                {isLimitExceeded ? (
                    <KeywordsLimitBanner
                        title={translate("seo.keywordsCampaign.keywords.banner.balance.title")}
                        buttonText={translate("seo.keywordsCampaign.keywords.banner.balance.button.title")}
                        buttonIconName="sparkles"
                        onButtonClick={() => onUpgradeHookClick("CampaignsUpgrade")}
                    />
                ) : null}

                <CampaignsBanner
                    CustomIcon={<Spend size="sm" />}
                    title={translate("seo.keywordsCampaign.campaigns.banner.balance.title")}
                    subtitle={bannerSubtitle}
                    buttonText={translate("seo.keywordsCampaign.campaigns.banner.balance.button.title")}
                    buttonIconName="sparkles"
                    onButtonClick={() => onUpgradeHookClick("CampaignsUpgrade")}
                />
            </ModalContent>

            <Modal.Footer withBorder>
                <Button
                    type="flat"
                    data-automation="add-keywords-modal-cancel"
                    onClick={onClose}
                >
                    {translate("common.cancel")}
                </Button>
                <Tooltip
                    enabled={isLimitExceeded}
                    content={translate("campaigns.keywordsStep.keywordsLimit.errorMsg")}
                >
                    <div>
                        <Button
                            isDisabled={!canSubmit}
                            isLoading={isPending}
                            onClick={handleSubmit}
                            data-automation="add-keywords-modal-submit"
                        >
                            {translate("rankTracker.addKeywords.modal.submit")}
                        </Button>
                    </div>
                </Tooltip>
            </Modal.Footer>

            {fixedGscConfig ? (
                <ImportGscModal
                    isOpen={keywords.isGscModalOpen}
                    fixedConfig={fixedGscConfig}
                    onClose={() => keywords.setIsGscModalOpen(false)}
                    onSubmit={handleGscImportSubmit}
                />
            ) : null}
        </>
    );
};
